import { isBotUA } from '@/utils/bot-detection';
import type { ReportSentryErrorOptions } from './reportSentryError';

// 只发送既有分类字段；原始异常、堆栈及请求/响应对象仍由 Sentry 处理。
const TAG_KEYS = [
  'monitoring_priority',
  'api.biz_code',
  'api.method',
  'api.route',
  'error.kind',
  'error.page',
  'error.source',
  'error.stage',
  'error.domain',
  'error.status_code',
  'http.status_code',
  'render.mode',
  'error_class',
  'error_class_reason',
  'error_origin',
  'handled',
  'interface_type',
  'level',
  'mechanism',
  'page_was_hidden',
] as const;
const EXTRA_KEYS = [
  'api_message',
  'api_path',
  'digest',
  'duration_ms',
  'error_code',
  'trace_id',
] as const;
const MAX_ERROR_MESSAGE_LENGTH = 200;

interface ClientDeviceContext {
  browserName?: string;
  browserVersion?: string;
  deviceFamily?: string;
  deviceName?: string;
  osName?: string;
  osVersion?: string;
}

function normalizedVersion(value: string | undefined): string | undefined {
  return value?.replaceAll('_', '.');
}

/** 从浏览器 UA 提取低基数设备上下文；解析失败时保持空值，由 Snowplow 继续兜底。 */
function getClientDeviceContext(userAgent: string): ClientDeviceContext {
  const browserMatch =
    userAgent.match(/GSA\/([\d.]+)/) ??
    userAgent.match(/Edg(?:A|iOS)?\/([\d.]+)/) ??
    userAgent.match(/(?:Chrome|CriOS)\/([\d.]+)/) ??
    userAgent.match(/(?:Firefox|FxiOS)\/([\d.]+)/) ??
    userAgent.match(/Version\/([\d.]+).*Safari\//);
  const browserName = /GSA\//.test(userAgent)
    ? 'Google'
    : /Edg(?:A|iOS)?\//.test(userAgent)
      ? 'Edge'
      : /(?:Chrome|CriOS)\//.test(userAgent)
        ? 'Chrome'
        : /(?:Firefox|FxiOS)\//.test(userAgent)
          ? 'Firefox'
          : /Safari\//.test(userAgent)
            ? 'Safari'
            : undefined;

  const iosMatch = userAgent.match(/(?:CPU (?:iPhone )?OS|iPhone OS) ([\d_]+)/);
  const androidMatch = userAgent.match(/Android ([\d.]+)/);
  const macMatch = userAgent.match(/Mac OS X ([\d_]+)/);
  const windowsMatch = userAgent.match(/Windows NT ([\d.]+)/);
  const isIPhone = /iPhone/.test(userAgent);
  const isIPad = /iPad/.test(userAgent);
  const androidDevice = userAgent.match(/Android [^;]+;\s*([^;)]+?)(?: Build[/;]|\))/)?.[1]?.trim();

  return {
    browserName,
    browserVersion: browserMatch?.[1],
    deviceFamily: isIPhone ? 'iPhone' : isIPad ? 'iPad' : androidMatch ? 'Android' : undefined,
    deviceName: isIPhone ? 'iPhone' : isIPad ? 'iPad' : androidDevice,
    osName: iosMatch
      ? 'iOS'
      : androidMatch
        ? 'Android'
        : windowsMatch
          ? 'Windows'
          : macMatch
            ? 'macOS'
            : undefined,
    osVersion: normalizedVersion(
      iosMatch?.[1] ?? androidMatch?.[1] ?? windowsMatch?.[1] ?? macMatch?.[1],
    ),
  };
}

function addClientRuntimeProperties(properties: Record<string, string | number | boolean>): void {
  const release = process.env.NEXT_PUBLIC_BUILD_RELEASE_TIME?.trim();
  if (release) properties.release = release;
  if (typeof navigator === 'undefined') return;

  const context = getClientDeviceContext(navigator.userAgent);
  if (context.browserName) properties.browser_name = context.browserName;
  if (context.browserVersion) properties.browser_version = context.browserVersion;
  if (context.deviceFamily) properties.device_family = context.deviceFamily;
  if (context.deviceName) properties.device_name = context.deviceName;
  if (context.osName) properties.os_name = context.osName;
  if (context.osVersion) properties.os_version = context.osVersion;
}

function sanitizeErrorMessage(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value) return undefined;
  return value
    .slice(0, MAX_ERROR_MESSAGE_LENGTH)
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[REDACTED_EMAIL]')
    .replace(/\b(?:bearer)\s+[a-z0-9._~+/=-]+/gi, 'Bearer [REDACTED]')
    .replace(
      /\b(token|secret|password|passwd|pwd|authorization)\s*[:=]\s*(?:"[^"]*"|'[^']*'|[^\s,;]+)/gi,
      '$1=[REDACTED]',
    )
    .replace(/\+?\d[\d\s().-]{7,}\d/g, '[REDACTED_PHONE]');
}

function addErrorSummaryProperties(
  properties: Record<string, string | number | boolean>,
  error: unknown,
): void {
  const errorRecord =
    error && typeof error === 'object' ? (error as Record<string, unknown>) : undefined;
  const name = error instanceof Error ? error.name : errorRecord?.name;
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : errorRecord?.message;
  if (typeof name === 'string' && name) properties.error_name = name.slice(0, 100);
  const sanitizedMessage = sanitizeErrorMessage(message);
  if (sanitizedMessage) properties.error_message = sanitizedMessage;
}

function addSentryCompatibleProperties(
  properties: Record<string, string | number | boolean>,
  priority: 'api_failure' | 'frontend_500',
): void {
  const isApiFailure = priority === 'api_failure';
  const kind = properties.error_kind;

  properties.error_class ??= isApiFailure ? 'operational' : 'business';
  properties.error_class_reason ??= isApiFailure
    ? kind === 'api_5xx' || kind === 'service_unavailable'
      ? 'axios_server'
      : 'axios_network'
    : 'explicit_business';
  properties.error_origin ??= 'manual';
  properties.handled ??= 'yes';
  properties.interface_type ??= 'exception';
  properties.level ??= 'error';
  properties.mechanism ??= 'generic';
  properties.page_was_hidden ??=
    typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

/** 双端可导入；只有客户端的两类监控错误才延迟加载观鹿入口，失败不影响 Sentry。 */
export function reportGuanluError(options: ReportSentryErrorOptions, error?: unknown): void {
  try {
    if (
      typeof window === 'undefined' ||
      (typeof navigator !== 'undefined' && isBotUA(navigator.userAgent))
    )
      return;
    const priority = options.tags?.monitoring_priority;
    if (priority !== 'api_failure' && priority !== 'frontend_500') return;

    const properties: Record<string, string | number | boolean> = {};
    const tags = { 'error.domain': options.domain, 'error.stage': options.stage, ...options.tags };
    for (const key of [...TAG_KEYS, ...EXTRA_KEYS]) {
      const value = key in tags ? tags[key as keyof typeof tags] : options.extra?.[key];
      if (typeof value === 'string') {
        properties[key.replaceAll('.', '_')] = (value.split(/[?#]/, 1)[0] ?? '').slice(0, 500);
      } else if (
        typeof value === 'boolean' ||
        (typeof value === 'number' && Number.isFinite(value))
      ) {
        properties[key.replaceAll('.', '_')] = value;
      }
    }
    addSentryCompatibleProperties(properties, priority);
    addClientRuntimeProperties(properties);
    addErrorSummaryProperties(properties, error);
    // 复用现有初始化前缓冲和开关，避免从服务端错误入口静态引入浏览器 SDK。
    void import('@/reporting/reliability/browserAnalytics')
      .then(({ captureBrowserAnalytics }) => {
        captureBrowserAnalytics(
          {
            kind: 'track',
            eventName: 'FrontendError_Monitor',
            properties,
            options: { omitPixel: true },
          },
          { channels: ['snowplow'] },
        );
      })
      .catch(() => {
        // 旁路加载/上报失败不递归上报，也不进入业务 Promise 链。
      });
  } catch {
    // 上下文读取失败同样不能阻断原 Sentry 上报。
  }
}
